import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, PhoneOff } from 'lucide-react';
import { supabase, callFunction } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { akteBeimAnrufAn, akteZiel } from '../../lib/akteBeimAnruf';
import './IncomingCallToast.css';

interface IncomingNotification {
  call_sid: string;
  from_number: string;
  customer_name: string | null;
  customer_id: string | null;
}

export function IncomingCallToast() {
  const { profile } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pending, setPending] = useState<IncomingNotification | null>(null);

  useEffect(() => {
    if (!profile?.id) return;
    const channel = supabase
      .channel(`cc-incoming-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'callcenter_telefonie',
          table: 'notifications',
          filter: `agent_id=eq.${profile.id}`,
        },
        (payload) => {
          const row = payload.new as { event_type: string; payload: IncomingNotification };
          if (row.event_type === 'incoming_call') setPending(row.payload);
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  if (!pending) return null;

  const accept = async () => {
    await callFunction('twilio-inbound-answer', { call_sid: pending.call_sid });
    // Beim Annehmen steht die Akte gleich offen — dasselbe wie beim eigenen
    // Anruf (Destiny, 08.09.2026), abschaltbar in den Einstellungen.
    const ziel = akteZiel({
      customerId: pending.customer_id,
      pfad: pathname,
      automatisch: akteBeimAnrufAn(),
    });
    setPending(null);
    if (ziel) navigate(ziel);
  };
  const decline = async () => {
    await callFunction('twilio-inbound-decline', { call_sid: pending.call_sid });
    setPending(null);
  };

  return (
    <div className="cc-toast" role="alertdialog" aria-label="Eingehender Anruf">
      <div className="cc-toast__title">Eingehender Anruf</div>
      <div className="cc-toast__name">
        {pending.customer_id ? (
          <Link
            className="cc-toast__akte"
            to={`/customers/${pending.customer_id}`}
            title="Kundenakte öffnen — der Anruf klingelt weiter"
          >
            {pending.customer_name ?? 'Unbekannt'}
          </Link>
        ) : (
          pending.customer_name ?? 'Unbekannt'
        )}
      </div>
      <div className="cc-toast__phone">{pending.from_number}</div>
      <div className="cc-toast__actions">
        <button className="cc-toast__btn cc-toast__btn--accept" onClick={() => void accept()}>
          <Phone size={16} /> Annehmen
        </button>
        <button className="cc-toast__btn cc-toast__btn--decline" onClick={() => void decline()}>
          <PhoneOff size={16} /> Ablehnen
        </button>
      </div>
    </div>
  );
}
